import React, { useCallback, useState } from 'react';
import {
    ActivityIndicator,
    View,
} from 'react-native';
import { centralAPI } from '../../services/central-api';
import CustomButton from '../../components/CustomButton';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import Toast from 'react-native-toast-message';
import { translate } from '../../services/translate';
import Card from '../../components/Card';
import Container from '../../components/Container';
import Header from '../../components/Header';
import MyAppText from '../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

type LatencyTestResult = {
    samples: number[];
    backendSamples: number[];
    min: number;
    avg: number;
    max: number;
    jitter: number;
    backendAvg: number;
};

type RequestTestResult = {
    total: number;
    success: number;
    failed: number;
    successRate: number;
};

type DownloadTestResult = {
    bytes: number;
    ms: number;
    mbps: number;
};

type DiagnosticResult = {
    latency: LatencyTestResult;
    requests: RequestTestResult;
    download1Mb: DownloadTestResult;
    download10Mb: DownloadTestResult;
};

const PING_COUNT = 30;
const PACKET_TEST_COUNT = 50;

const sleep = (ms: number) =>
    new Promise(resolve => setTimeout(resolve, ms));

const formatMs = (value: number) =>
    `${Math.round(value)} ms`;

const formatMbps = (value: number) =>
    `${value.toFixed(2)} Mbps`;

const getJitterStatus = (jitter: number) => {
    if (jitter < 30) {
        return 'Estável';
    }

    if (jitter < 100) {
        return 'Oscilação moderada';
    }

    return 'Muito instável';
};

const getDiagnosis = (result: DiagnosticResult) => {
    const { latency, requests } = result;

    /*
     * Se o Express está processando rapidamente, mas o browser
     * está demorando muito, temos um forte indicativo de que
     * o problema está fora da aplicação.
     */
    if (latency.backendAvg < 50 && latency.avg > 500) {
        return {
            title: translate('possibleNetworkProblem'),
            description: translate('possibleNetworkProblemDescription'),
        };
    }

    if (requests.successRate < 100) {
        return {
            title: translate('possibleConnectionInstability'),
            description: translate('possibleConnectionInstabilityDescription'),
        };
    }

    if (latency.jitter > 100) {
        return {
            title: translate('unstableConnection'),
            description: translate('unstableConnectionDescription'),
        };
    }

    if (latency.avg > 500) {
        return {
            title: translate('highLatency'),
            description: translate('highLatencyDescription'),
        };
    }

    return {
        title: translate('apparentlyHealthyConnection'),
        description: translate('apparentlyHealthyConnectionDescription'),
    };
};

export default function ConnectionDiagnostics() {
    const { styles } = useStyles(stylesheet);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState('');
    const [result, setResult] =
        useState<DiagnosticResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const copyDiagnostic = useCallback(async () => {
        if (!result) {
            return;
        }

        const diagnosis = getDiagnosis(result);

        const now = new Date();

        const browser = navigator.userAgent;
        const platform = navigator.platform;
        const language = navigator.language;

        const networkTime = Math.max(
            0,
            result.latency.avg -
            result.latency.backendAvg,
        );

        const text = [
            translate('connectionDiagnostics'),
            '==================================',
            '',
            `${translate('date')}: ${now.toLocaleString()}`,
            `${translate('browser')}: ${browser}`,
            `${translate('platform')}: ${platform}`,
            `${translate('language')}: ${language}`,
            '',
            translate('latency').toUpperCase(),
            '--------',
            `${translate('minimum')}: ${formatMs(
                result.latency.min,
            )}`,
            `${translate('average')}: ${formatMs(
                result.latency.avg,
            )}`,
            `${translate('maximum')}: ${formatMs(
                result.latency.max,
            )}`,
            `${translate('jitter')}: ${formatMs(
                result.latency.jitter,
            )}`,
            '',
            translate('server').toUpperCase(),
            '--------',
            `${translate('averageBackend')}: ${formatMs(
                result.latency.backendAvg,
            )}`,
            `${translate('estimatedNetworkTime')}: ${formatMs(
                networkTime,
            )}`,
            '',
            translate('stability').toUpperCase(),
            '------------',
            `${translate('requests')}: ${result.requests.total
            }`,
            `${translate('success')}: ${result.requests.success
            }`,
            `${translate('failures')}: ${result.requests.failed
            }`,
            `${translate('successRate')}: ${result.requests.successRate.toFixed(
                1,
            )}%`,
            '',
            translate('download').toUpperCase(),
            '--------',
            `1 MB: ${formatMbps(
                result.download1Mb.mbps,
            )} (${formatMs(
                result.download1Mb.ms,
            )})`,
            `10 MB: ${formatMbps(
                result.download10Mb.mbps,
            )} (${formatMs(
                result.download10Mb.ms,
            )})`,
            '',
            translate('diagnosis').toUpperCase(),
            '-----------',
            diagnosis.title,
            '',
            diagnosis.description,
            '',
            '==================================',
            `${translate('utcTime')}: ${now.toISOString()}`,
        ].join('\n');

        try {
            await navigator.clipboard.writeText(text);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('copiedToClipboard'),
            });
        } catch (error) {
            console.error('Could not copy the diagnostics.', error);
        }
    }, [result, translate]);

    const runLatencyTest =
        useCallback(async (): Promise<LatencyTestResult> => {
            const samples: number[] = [];
            const backendSamples: number[] = [];

            for (let i = 0; i < PING_COUNT; i++) {
                setProgress(translate('latencyTestProgress', {
                    current: i + 1,
                    total: PING_COUNT,
                }));

                try {
                    const sample = await centralAPI.ping();

                    samples.push(sample.latency);
                    backendSamples.push(sample.backendTime);
                } catch {
                    // a fail doesn't count as an error
                }

                await sleep(150);
            }

            if (!samples.length) {
                throw new Error(translate('noDiagnosticRequestCompleted'));
            }

            const min = Math.min(...samples);
            const max = Math.max(...samples);
            const avg = samples.reduce((sum, value) => sum + value, 0) / samples.length;
            let jitter = 0;

            if (samples.length > 1) {
                let totalDifference = 0;

                for (let i = 1; i < samples.length; i++) {
                    totalDifference += Math.abs(samples[i] - samples[i - 1]);
                }

                jitter = totalDifference / (samples.length - 1);
            }

            const backendAvg = backendSamples.length ? backendSamples.reduce((sum, value) => sum + value, 0) / backendSamples.length : 0;

            return {
                samples,
                backendSamples,
                min,
                avg,
                max,
                jitter,
                backendAvg,
            };
        }, []);

    const runRequestTest =
        useCallback(async (): Promise<RequestTestResult> => {
            setProgress(translate('stabilityTestProgress', { total: PACKET_TEST_COUNT }));

            const results = await Promise.allSettled(
                Array.from({ length: PACKET_TEST_COUNT }, () => centralAPI.ping()),
            );

            const success = results.filter(result => result.status === 'fulfilled').length;

            const failed = PACKET_TEST_COUNT - success;

            return {
                total: PACKET_TEST_COUNT,
                success,
                failed,
                successRate: (success / PACKET_TEST_COUNT) * 100,
            };
        }, []);

    const runTests = useCallback(async () => {
        if (running) {
            return;
        }

        setRunning(true);
        setError(null);
        setResult(null);

        try {
            setProgress(translate('startingDiagnostics'));
            const latency = await runLatencyTest();
            const requests = await runRequestTest();

            setProgress(translate('testing1MbDownload'));
            const download1Mb = await centralAPI.diagnosticsDownload('1mb');

            setProgress(translate('testing10MbDownload'));
            const download10Mb = await centralAPI.diagnosticsDownload('10mb');

            setProgress(translate('finishingDiagnostics'));

            setResult({
                latency,
                requests,
                download1Mb,
                download10Mb,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : translate('unknownErrorDiagnostics'),);
        } finally {
            setRunning(false);
            setProgress('');
        }
    }, [
        running,
        runLatencyTest,
        runRequestTest,
    ]);

    const renderMetric = (
        label: string,
        value: string,
        description?: string,
    ) => (
        <Card style={styles.metric}>
            <MyAppText style={styles.metricLabel}>
                {label}
            </MyAppText>

            <MyAppText style={styles.metricValue}>
                {value}
            </MyAppText>

            {description && (
                <MyAppText style={styles.metricDescription}>
                    {description}
                </MyAppText>
            )}
        </Card>
    );

    return <Container>
        <Header title={translate('connectionDiagnostics')} />

        <Card scrollView={true}>
            <View
                style={{
                    flex: 1,
                    maxWidth: 900,
                    gap: 15,
                    alignSelf: 'center',
                }}
            >
                <View>
                    <MyAppText style={styles.sectionTitle}>
                        {translate('connectionDiagnostics')}
                    </MyAppText>

                    <MyAppText>
                        {translate('connectionDiagnosticsDescription')}
                    </MyAppText>
                </View>

                <View style={styles.infoBox}>
                    <MyAppText style={styles.infoTitle}>
                        {translate('important')}
                    </MyAppText>

                    <MyAppText style={styles.infoText}>
                        {translate('connectionDiagnosticsSpeedTestDescription')}
                    </MyAppText>
                </View>

                <CustomButton
                    onPress={runTests}
                    disabled={running}
                    isLoading={running}
                    text={translate('startDiagnostics')}
                />

                {progress && (
                    <Card style={styles.progressContainer}>
                        <ActivityIndicator />

                        <MyAppText style={styles.progressText}>
                            {progress}
                        </MyAppText>
                    </Card>
                )}

                {error && (
                    <View style={styles.errorBox}>
                        <MyAppText style={styles.errorTitle}>
                            {translate('diagnosticsCouldNotBeCompleted')}
                        </MyAppText>

                        <MyAppText style={styles.errorText}>
                            {error}
                        </MyAppText>
                    </View>
                )}

                {result && (
                    <>
                        <Card style={styles.diagnosisBox}>
                            <MyAppText style={styles.diagnosisTitle}>
                                {getDiagnosis(result).title}
                            </MyAppText>

                            <MyAppText style={styles.diagnosisText}>
                                {getDiagnosis(result).description}
                            </MyAppText>
                        </Card>

                        <CustomButton
                            icon={faCopy}
                            onPress={async () => {
                                await copyDiagnostic();
                            }}
                            type="secondary"
                            text={translate('copyToClipboard')}
                        />

                        <MyAppText style={styles.sectionTitle}>
                            {translate('latency')}
                        </MyAppText>

                        <View style={styles.grid}>
                            {renderMetric(translate('minimum'), formatMs(result.latency.min))}
                            {renderMetric(translate('average'), formatMs(result.latency.avg))}
                            {renderMetric(translate('maximum'), formatMs(result.latency.max))}
                            {renderMetric(translate('jitter'), formatMs(result.latency.jitter), getJitterStatus(result.latency.jitter,))}
                            {renderMetric(translate('backend'), formatMs(result.latency.backendAvg,), translate('averageServerProcessingTime'))}
                        </View>

                        <View style={styles.comparisonBox}>
                            <MyAppText style={styles.comparisonTitle}>
                                {translate('whereIsTheTime')}
                            </MyAppText>

                            <MyAppText style={styles.comparisonText}>
                                {translate('browserToServer')}
                            </MyAppText>

                            <MyAppText style={styles.comparisonValue}>
                                {formatMs(result.latency.avg)}
                            </MyAppText>

                            <MyAppText style={styles.comparisonText}>
                                {translate('backendProcessing')}
                            </MyAppText>

                            <MyAppText style={styles.comparisonValue}>
                                {formatMs(result.latency.backendAvg)}
                            </MyAppText>

                            <MyAppText style={styles.comparisonHint} >
                                {translate('approximateDifference')}
                                {' '}
                                {formatMs(Math.max(0, result.latency.avg - result.latency.backendAvg))}
                            </MyAppText>
                        </View>

                        <MyAppText style={styles.sectionTitle}>
                            {translate('stability')}
                        </MyAppText>

                        <View style={styles.grid}>
                            {renderMetric(translate('requests'), `${result.requests.success}/${result.requests.total}`)}
                            {renderMetric(translate('success'), `${result.requests.successRate.toFixed(1)}%`)}
                            {renderMetric(translate('failures'), `${result.requests.failed}`)}
                        </View>

                        <MyAppText style={styles.sectionTitle}>
                            {translate('download')}
                        </MyAppText>

                        <View style={styles.grid}>
                            {renderMetric('1 MB', formatMbps(result.download1Mb.mbps), formatMs(result.download1Mb.ms))}
                            {renderMetric('10 MB', formatMbps(result.download10Mb.mbps), formatMs(result.download10Mb.ms))}
                        </View>

                        <View style={{ gap: 15 }}>
                            <MyAppText style={styles.sectionTitle}>
                                {translate('environmentInformation')}
                            </MyAppText>

                            {renderMetric(translate('browser'), navigator.userAgent)}
                            {renderMetric(translate('platform'), navigator.platform)}
                            {renderMetric(translate('language'), navigator.language)}
                            {renderMetric(translate('logicalCpu'), String(navigator.hardwareConcurrency ?? 'N/A'))}
                        </View>

                        <MyAppText style={styles.footer}>
                            {translate('diagnosticsPerformedAt')}
                            {' '}
                            {new Date().toLocaleString()}
                        </MyAppText>
                    </>
                )}
            </View>
        </Card>
    </Container>;
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
    },

    subtitle: {
        fontSize: 16,
        lineHeight: 24,
        color: theme.colors.labelColor,
    },

    infoBox: {
        padding: 18,
        borderRadius: 10,
        backgroundColor: theme.colors.badges.blue.backgroundColor,
    },

    infoTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: theme.colors.badges.blue.textColor,
    },

    infoText: {
        fontSize: 14,
        lineHeight: 21,
        color: theme.colors.badges.blue.textColor,
    },

    progressContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        borderColor: theme.colors.backgrounds['BG-1'],
        borderWidth: 1,
    },

    progressText: {
        color: theme.colors.labelColor,
        fontSize: 14,
    },

    diagnosisBox: {
        borderWidth: 1,
        padding: 20,
        borderColor: theme.colors.backgrounds['BG-1'],
    },

    diagnosisTitle: {
        fontSize: 20,
        fontWeight: '700',
    },

    diagnosisText: {
        fontSize: 15,
        lineHeight: 22,
        color: theme.colors.labelColor,
    },

    sectionTitle: {
        fontSize: 19,
        fontWeight: '700',
    },

    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },

    metric: {
        flexGrow: 1,
        flexBasis: 160,
        padding: 18,
        minHeight: 100,
        borderColor: theme.colors.backgrounds['BG-1'],
        borderWidth: 1,
    },

    metricLabel: {
        fontSize: 13,
        color: theme.colors.labelColor,
    },

    metricValue: {
        fontSize: 22,
        fontWeight: '700',
    },

    metricDescription: {
        fontSize: 12,
        color: theme.colors.labelColor,
    },

    comparisonBox: {
        padding: 20,
        borderColor: theme.colors.backgrounds['BG-1'],
        borderWidth: 1,
        borderRadius: 8,
    },

    comparisonTitle: {
        fontSize: 18,
        fontWeight: '700',
    },

    comparisonText: {
        fontSize: 13,
        color: theme.colors.labelColor,
    },

    comparisonValue: {
        fontSize: 24,
        fontWeight: '700',
    },

    comparisonHint: {
        fontSize: 14,
        color: theme.colors.labelColor,
    },

    footer: {
        fontSize: 12,
        color: theme.colors.labelColor,
    },

    errorBox: {
        padding: 18,
        borderRadius: 10,
        backgroundColor: theme.colors.badges.red.backgroundColor,
    },

    errorTitle: {
        fontWeight: '700',
        color: theme.colors.badges.red.textColor,
    },

    errorText: {
        color: theme.colors.badges.red.textColor,
    },
}));
